export const FORKHIRE_ANALYTICS_EXCLUDED_APPLICATION_STATUSES = [
  'cv_scoring_pending',
  'cv_scoring_failed',
  'duplicate_review_pending',
  'rejected_unreadable',
] as const;

export const FORKHIRE_ANALYTICS_INTERVIEW_OR_LATER_STAGES = [
  'interviewed',
  'ai_interview_completed',
  'second_interview',
  'f2f_interview',
  'offering',
  'signing_contract',
  'hired',
] as const;

const EXCLUDED_APPLICATION_STATUS_SET = new Set<string>(FORKHIRE_ANALYTICS_EXCLUDED_APPLICATION_STATUSES);
const INTERVIEW_OR_LATER_STAGE_SET = new Set<string>(FORKHIRE_ANALYTICS_INTERVIEW_OR_LATER_STAGES);

export type ForkHireAnalyticsApplicationMetricInput = {
  stage?: string | null;
  status?: string | null;
  hasCvScore?: boolean;
  hasQualifyingInterviewSession?: boolean;
};

export type ForkHireAnalyticsChartSummaryInput = {
  totalApplications: number;
  scored: number;
  interviewed: number;
  hired: number;
  rejected: number;
};

export type ForkHireAnalyticsChartTotals = {
  applications: number;
  scored: number;
  notScored: number;
  interviewed: number;
  notInterviewed: number;
  hired: number;
  rejected: number;
  unresolved: number;
};

function normalizeMetricValue(value: string | null | undefined) {
  return (value ?? '').trim().toLowerCase();
}

function normalizeCount(value: number) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function clampToApplications(value: number, applications: number) {
  return Math.min(normalizeCount(value), applications);
}

export function isForkHireAnalyticsAcceptedApplication(application: ForkHireAnalyticsApplicationMetricInput) {
  return !EXCLUDED_APPLICATION_STATUS_SET.has(normalizeMetricValue(application.status));
}

export function isForkHireAnalyticsInterviewed(application: ForkHireAnalyticsApplicationMetricInput) {
  return Boolean(application.hasQualifyingInterviewSession)
    || INTERVIEW_OR_LATER_STAGE_SET.has(normalizeMetricValue(application.stage));
}

export function isForkHireAnalyticsHired(application: ForkHireAnalyticsApplicationMetricInput) {
  const stage = normalizeMetricValue(application.stage);
  const status = normalizeMetricValue(application.status);
  return stage === 'hired' || status === 'hired';
}

export function isForkHireAnalyticsRejected(application: ForkHireAnalyticsApplicationMetricInput) {
  const stage = normalizeMetricValue(application.stage);
  const status = normalizeMetricValue(application.status);
  return stage === 'rejected' || status === 'rejected';
}

export function getForkHireAnalyticsPercentage(count: number, totalApplications: number) {
  const applications = normalizeCount(totalApplications);
  if (applications <= 0) return 0;
  return Math.round((clampToApplications(count, applications) / applications) * 100);
}

export function getForkHireAnalyticsBarWidth(percentage: number) {
  return percentage <= 0 ? 0 : Math.min(100, Math.max(0, percentage));
}

export function getForkHireAnalyticsChartTotals(
  summary: ForkHireAnalyticsChartSummaryInput,
): ForkHireAnalyticsChartTotals {
  const applications = normalizeCount(summary.totalApplications);
  const scored = clampToApplications(summary.scored, applications);
  const interviewed = clampToApplications(summary.interviewed, applications);
  const hired = clampToApplications(summary.hired, applications);
  const rejected = clampToApplications(summary.rejected, applications);

  return {
    applications,
    scored,
    notScored: Math.max(applications - scored, 0),
    interviewed,
    notInterviewed: Math.max(applications - interviewed, 0),
    hired,
    rejected,
    unresolved: Math.max(applications - hired - rejected, 0),
  };
}
